import { HttpClient, HttpResourceRef, httpResource } from "@angular/common/http";
import { Injectable, Signal, inject } from "@angular/core";
import { APP_CONFIG_TOKEN } from "@kno2/core/config";
import { filterNonEmptyProperties } from "@kno2/utils";
import { Observable, firstValueFrom } from "rxjs";

export interface Eula {
    id: number;
    version: string;
    active: boolean;
    url: string;
    type: "EULA" | "Privacy";
    brand: { name: string };
    totalUsers: number;
}

export interface PageOptions {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
    sortDir?: "asc" | "desc";
    sortBy?: string;
}

@Injectable({
    providedIn: "root"
})
export class EulasService {
    private httpClient = inject(HttpClient);
    private appConfig = inject(APP_CONFIG_TOKEN);

    public getPageSizeOptions() {
        return [10, 25, 50, 100];
    }

    public getPages(query: Signal<PageOptions>): HttpResourceRef<{ items: Eula[]; total: number }> {
        return httpResource(
            () => {
                const params = filterNonEmptyProperties(query());
                return {
                    url: "/api/admin/eulas",
                    params: params
                };
            },
            {
                defaultValue: { items: [], total: 0 }
            }
        );
    }
    public getById = (id: string): Promise<Eula> => firstValueFrom(this.getById$(id));
    public save = (eula: Eula, file: File): Promise<Eula> => firstValueFrom(this.save$(eula, file));
    public delete = (id: number): Promise<void> => firstValueFrom(this.delete$(id));
    public activate = (id: number): Promise<void> => firstValueFrom(this.activate$(id));
    public isEulaVersionExists = (id: number, version: string, brandName: string): Promise<boolean> =>
        firstValueFrom(this.isEulaVersionExists$(id, version, brandName));
    public accept = (id: number): Promise<void> => firstValueFrom(this.accept$(id));
    public getActive = (type: Eula["type"] = "EULA"): Promise<Eula> => firstValueFrom(this.getActive$(type));
    public getViewActive = (type: Eula["type"] = "EULA"): Promise<string> => firstValueFrom(this.getViewActive$(type));
    public view = (id: number): Promise<string> => firstValueFrom(this.view$(id));

    private getById$(id: string): Observable<Eula> {
        return this.httpClient.get<Eula>(`/api/admin/eulas/${id}`);
    }

    private isEulaVersionExists$(id: number, version: string, brandName: string): Observable<boolean> {
        return this.httpClient.get<boolean>(`/api/eulas/exists`, {
            params: { id, version, brandName }
        });
    }

    private getActive$(type: Eula["type"] = "EULA"): Observable<Eula> {
        return this.httpClient.get<Eula>(`/api/eulas/active`, {
            params: { type }
        });
    }

    private getViewActive$(type: Eula["type"] = "EULA"): Observable<string> {
        return this.httpClient.get(`/api/eulas/active/view`, {
            params: {
                type,
                brandName: this.appConfig.brandDisplayName
            },
            responseType: "text"
        });
    }

    private view$(id: number): Observable<string> {
        return this.httpClient.get(`/api/admin/eulas/${id}/view`, {
            responseType: "text"
        });
    }

    private save$(eula: Eula, file: File): Observable<Eula> {
        const formData = new FormData();
        formData.append("eula", JSON.stringify(eula));
        formData.append("file", file);
        return this.httpClient.post<Eula>("/api/admin/eulas", formData);
    }

    private activate$(id: number): Observable<void> {
        return this.httpClient.post<void>(`/api/admin/eulas/${id}/activate`, null);
    }

    private accept$(id: number): Observable<void> {
        return this.httpClient.post<void>(`/api/eulas/${id}/accept`, null);
    }

    private delete$(id: number): Observable<void> {
        return this.httpClient.delete<void>(`/api/admin/eulas/${id}`);
    }
}
